/* Shell.jsx — Header, footer, marquee, navigation */

// Tiny hook: returns a CMS value that re-renders when /api/cms finishes loading.
function useCMS(key, fallback) {
  const initial = (window.CMS && window.CMS.get) ? window.CMS.get(key, fallback) : fallback;
  const [v, setV] = React.useState(initial);
  React.useEffect(() => {
    const onReady = () => {
      if (window.CMS && window.CMS.get) setV(window.CMS.get(key, fallback));
    };
    if (window.CMS && window.CMS._ready) onReady();
    else window.addEventListener("cms:ready", onReady);
    return () => window.removeEventListener("cms:ready", onReady);
  }, [key, fallback]);
  return v;
}
window.useCMS = useCMS;

// Order set by JJ 2026-08-05. Camps moved back from second to next-to-last now
// that 2026 registration is closed, so the live programming leads instead.
const NAV_ITEMS = [
  { id: "home", label: "Home" },
  { id: "programs", label: "Programs" },
  { id: "gallery", label: "Gallery" },
  { id: "mece-choir", label: "MECE Choir" },
  { id: "venice", label: "Venice Island" },
  { id: "camps", label: "Camps" },
  // Not named in that ordering, kept in place rather than dropped -- removing a
  // page from the nav is not something to infer from an omission.
  { id: "collaboration", label: "Partners" },
  // CR #343: "Workshops" removed from the header per JJ. The /workshop route
  // stays valid (page still reachable directly), just unlinked from the nav.
  // CR #339 (CR-30243): public entry point for the Fire Prevention Play
  // submission + voting page so rec sites can find and submit. Route is
  // "fireplay" (renamed from "firecamp"; old #firecamp links still resolve
  // via the alias in App.jsx).
  { id: "fireplay", label: "Fire Play" },
  { id: "interest", label: "Dance at VI" },
];

// THE ONE NAV ITEM THAT COMES AND GOES. The open mic is a single night, so a
// permanent header link would be a dead end from October 27 onward -- and the
// header is on every page, so it outlasts the popup (once per visitor) and
// sits alongside the footer link that retires on the same date.
//
// It is also what keeps the row fitting. NAV_ITEMS is seven and the desktop
// nav is only shown above 980px; this makes it eight for a month and then
// puts it back. Measured at 981px (the tightest desktop width) with the item
// in: still one row. If a NINTH permanent item is ever added, re-measure --
// that is the one this borrowed the space from.
//
// Reads the same window.PPR_OPEN_MIC.closesAt as the popup, the footer link
// and the form, through the same fail-closed guard: an unparseable date hides
// the link rather than pinning it up forever.
function liveNavItems() {
  let items = NAV_ITEMS;

  const mic = window.PPR_OPEN_MIC;
  if (mic && window.pprNoticeIsLive
      && window.pprNoticeIsLive(mic.closesAt, Date.now())
      && !mic.signupsClosed) {
    items = [...items, { id: mic.route || "openmic", label: "Open Mic", icon: "mic" }];
  }

  const hol = window.PPR_HOLIDAY_SHOW;
  if (hol && window.pprNoticeIsLive
      && window.pprNoticeIsLive(hol.closesAt, Date.now())
      && !hol.signupsClosed) {
    items = [...items, { id: hol.route || "holidayshow", label: "Holiday Show", icon: "star" }];
  }

  return items;
}

// NAV_ITEMS is nine now (Dance at VI added). 1080 fits nine at the tightest
// width; 1280 if the open mic pushes it to ten.
function navBreakpoint() {
  return liveNavItems().length > NAV_ITEMS.length ? 1280 : 1080;
}

// House icon standard: an inline SVG on currentColor, never a glyph or an
// emoji. It inherits the link's colour, so it flips with the pill on the
// active route for free, and it is aria-hidden because the word "Open Mic"
// beside it already names the link -- a screen reader announcing "microphone
// Open Mic" is noise.
function StarIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0, marginTop: -1 }}
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0, marginTop: -1 }}
    >
      <rect x="9" y="2" width="6" height="11" rx="3" />
      <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
      <path d="M12 18v3" />
    </svg>
  );
}

/* THE HEADER FLOATS OVER THE HERO AND ONLY TAKES ITS CREAM BACKGROUND ONCE
   YOU SCROLL. Two problems come with that, and both are solved here rather
   than in the markup.

   ONE: it is `fixed`, so it is out of the flow and every hero now starts at the
   top of the viewport, underneath it. That is the point -- it is how you see
   the hero through the bar -- but it means a hero whose own top padding is
   less than the bar's height would slide its title under it. Every hero on
   this site already opens with >=80px of top padding against a 78px bar, which
   is why this did not need eighteen separate edits; scripts/header-overlay
   is not a thing, so the guard is the route sweep in the PR, and a NEW hero
   with tight top padding is the thing to re-check.

   TWO: the nav is ink, and Home, Gallery, Camps and the open mic all open on a
   dark hero. Ink on dark is invisible, so the bar has to know what it is
   sitting on.

   IT MEASURES RATHER THAN REMEMBERING. A hard-coded list of dark routes is a
   list somebody has to update every time a hero is restyled, and nothing fails
   loudly when they forget -- the nav just disappears on one page. So this
   reads the actual background behind the bar on every route change: the first
   laid-out element inside <main>, walking up for a non-transparent colour, and
   falling back to the first colour in a background-image gradient, which is
   what a gradient hero like the open mic's reports. Luminance decides.

   It fails to LIGHT (ink text on a cream bar), because that is the current
   behaviour and the safe direction: a bar that wrongly thinks it is on dark
   renders cream-on-cream and vanishes. */

const HEADER_SCROLL_AT = 24;      // px scrolled before the bar takes its background

function parseRgb(str) {
  const n = String(str || "").match(/\d+(\.\d+)?/g);
  if (!n || n.length < 3) return null;
  if (n.length >= 4 && Number(n[3]) === 0) return null;   // fully transparent
  return [Number(n[0]), Number(n[1]), Number(n[2])];
}

// sRGB relative luminance, then WCAG contrast. Worth doing properly: the
// first version of this thresholded on luminance alone and picked cream for
// anything under 140, which put cream on the Camps hero's terracotta at 3.62:1
// and on the Promote hero's sage at 3.38:1. Both read as washed-out on a
// screen in daylight, and both are below AA for the 14.5px nav.
function srgbL(c) { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; }
function relLum([r, g, b]) { return 0.2126 * srgbL(r) + 0.7152 * srgbL(g) + 0.0722 * srgbL(b); }
function contrast(a, b) {
  const [x, y] = [relLum(a), relLum(b)];
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

const INK   = [42, 24, 32];
const CREAM = [251, 243, 226];
const AA    = 4.5;
const SCRIM = 0.42;   // black alpha behind the bar when neither colour passes

function over(fg, bg, alpha) {
  return fg.map((c, i) => Math.round(c * alpha + bg[i] * (1 - alpha)));
}

/* WHAT THE BAR IS SITTING ON, MEASURED, NOT REMEMBERED. A hard-coded list of
   dark routes is a list somebody has to update every time a hero is restyled,
   and nothing fails loudly when they forget -- the nav just disappears on one
   page. This reads the real background: the first laid-out element inside
   <main>, walking up for a non-transparent colour, falling back to the first
   colour stop of a background-image gradient, which is what a gradient hero
   like the open mic's reports.

   Then it CHOOSES rather than thresholds. Ink and cream are both scored
   against that background and the better one wins, which is what puts ink back
   on the sage and terracotta heroes where cream was failing. When neither
   clears AA -- terracotta is the real case, 4.20 ink against 3.62 cream -- the
   bar earns its own contrast with a soft scrim and goes cream over that.

   Fails to ink-on-nothing, which is what the bar did before any of this. */
function headerToneFor() {
  const header = document.querySelector("header");
  const main = document.querySelector("main");
  if (!header || !main) return { light: false, scrim: 0 };

  // LOOK THROUGH THE BAR rather than guessing at the DOM. The first version
  // walked down from <main> for an element with a background, and got the MECE
  // Choir page wrong: its hero paints on a descendant the walk never reached,
  // so the bar decided the page was light and rendered ink on near-black at
  // 1:1. Asking the browser what is painted at that spot is the actual
  // question.
  //
  // elementsFromPoint, PLURAL, and then the first hit inside <main>. The
  // singular version returns the topmost element, which on this site is
  // whichever full-screen overlay happens to be up -- the home-page popups are
  // `position: fixed; inset: 0`, so the bar was reading the modal's
  // rgba(0,0,0,0.55) scrim and going cream on every route the popup fired on.
  // Worse, it depended on whether the modal had mounted yet, so the same page
  // could measure either way between loads. Filtering to <main> skips every
  // overlay and the header itself, with no pointer-events juggling.
  const rect = header.getBoundingClientRect();
  const stack = document.elementsFromPoint(
    Math.round(window.innerWidth / 2),
    Math.round(rect.height / 2)
  );
  const el = stack.find((n) => main.contains(n));

  // Nothing there (a short page that does not reach under the bar) is the page
  // background, which is cream.
  if (!el || el === document.documentElement || el === document.body) {
    return { light: false, scrim: 0 };
  }

  let node = el, rgb = null;
  while (node && node !== document.documentElement && !rgb) {
    const cs = getComputedStyle(node);
    rgb = parseRgb(cs.backgroundColor);
    if (!rgb && cs.backgroundImage && cs.backgroundImage !== "none") {
      // A gradient reports no background-color; its first stop is what sits at
      // the top of the hero, which is the part under the bar. A photograph
      // reports url(...) and no colour, so it falls through to the ancestor --
      // and if that is dark, cream wins, which is the safe way round over a
      // photo.
      const m = cs.backgroundImage.match(/rgba?\([^)]+\)|#[0-9a-fA-F]{3,8}/);
      if (m) {
        if (m[0][0] === "#") {
          const h = m[0].slice(1);
          const f = h.length === 3 ? h.split("").map(c => c + c) : h.match(/../g);
          rgb = f.slice(0, 3).map(v => parseInt(v, 16));
        } else rgb = parseRgb(m[0]);
      }
    }
    node = node.parentElement;
  }
  if (!rgb) return { light: false, scrim: 0 };

  const cInk = contrast(INK, rgb), cCream = contrast(CREAM, rgb);
  if (Math.max(cInk, cCream) >= AA) return { light: cCream > cInk, scrim: 0 };
  return { light: true, scrim: SCRIM };
}

function useHeaderChrome(route) {
  const [scrolled, setScrolled] = useState(
    () => typeof window !== "undefined" && window.scrollY > HEADER_SCROLL_AT
  );
  const [tone, setTone] = useState({ light: false, scrim: 0 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > HEADER_SCROLL_AT);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    // Two frames: one for React to commit the new route, one for the browser
    // to lay it out. Reading on the same tick measures the page you just left.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setTone(headerToneFor()));
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [route]);

  // Once the cream background is in, the bar is its own surface and the page
  // behind it stops mattering.
  return {
    scrolled,
    light: tone.light && !scrolled,
    scrim: scrolled ? 0 : tone.scrim,
  };
}

function Logo({ onClick, light }) {
  return (
    <a
      href="#home"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        textDecoration: "none",
        color: light ? "var(--c-cream)" : "var(--c-ink)",
        transition: "color 200ms",
      }}
    >
      {/* CR #224: header logo is the "Find Your Path" badge from the footer,
          recolored to terracotta orange (#D8552B). Same 44x44 size as the
          previous custom SVG logo. The red variant went with the shop's
          page-scoped palette (CR #268), retired 2026-08-14. */}
      <img
        src="/img/find-your-path-orange.png"
        alt="Find Your Path -- Performing Arts"
        width="44"
        height="44"
        style={{ display: "block" }}
      />
      <div style={{ lineHeight: 1.05 }}>
        <div
          style={{
            fontFamily: "var(--f-display)",
            fontWeight: 800,
            fontSize: 18,
            letterSpacing: "-0.02em",
          }}
        >
          Performing Arts
        </div>
        <div
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 9.5,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            // The strapline is the quietest thing in the bar and the first to
            // disappear, so on a dark hero it goes to a tint of cream rather
            // than the ink-soft grey, which would sit at about 2:1 there.
            color: light ? "rgba(251,243,226,0.78)" : "var(--c-ink-soft)",
            transition: "color 200ms",
          }}
        >
          Philadelphia Parks &amp; Recreation
        </div>
      </div>
    </a>
  );
}

function Header({ route, setRoute }) {
  const [open, setOpen] = React.useState(false);
  const { scrolled, light: onDarkHero, scrim } = useHeaderChrome(route);
  // An open mobile sheet forces the bar solid, so it also stops being "on
  // dark": the sheet is paper, and a transparent bar above it would leave the
  // logo floating over the hero with a solid panel hanging off it.
  const solid = scrolled || open;
  const light = onDarkHero && !solid;
  const handleNav = (id) => (e) => {
    e.preventDefault();
    setRoute(id);
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      style={{
        // FIXED, not sticky. Sticky keeps the bar in the flow, so the hero
        // starts below it and "transparent" would only ever show the cream
        // page behind -- the whole point is the hero running under the bar.
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        background: solid
          ? "var(--c-cream)"
          : (scrim && !open
              // Top-down so it fades out rather than ending on a hard edge --
              // a flat band would just look like a second, greyer header.
              ? `linear-gradient(180deg, rgba(0,0,0,${scrim}) 0%, rgba(0,0,0,${scrim * 0.55}) 60%, rgba(0,0,0,0) 100%)`
              : "transparent"),
        borderBottom: solid ? "2px solid var(--c-ink)" : "2px solid transparent",
        transition: "background 220ms ease, border-color 220ms ease",
      }}
    >
      <div
        className="container"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          // Top/bottom only. A horizontal value here pins the header to 32px
          // while .container drops to 20px under 720px, so the nav stops
          // lining up with the page content it sits above.
          paddingTop: 16,
          paddingBottom: 16,
          gap: 24,
        }}
      >
        <Logo onClick={handleNav("home")} light={light} />

        <nav
          className="nav-desktop"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {liveNavItems().map((item) => {
            const active = route === item.id;
            return (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={handleNav(item.id)}
                style={{
                  // Padding, weight and size are untouched from the seven-item
                  // header. The eighth item has to fit inside the row as it
                  // is, not by making the row lighter or tighter.
                  padding: "9px 14px",
                  borderRadius: 999,
                  textDecoration: "none",
                  fontFamily: "var(--f-display)",
                  fontWeight: 600,
                  fontSize: 14.5,
                  letterSpacing: "-0.005em",
                  // Over a dark hero the pill inverts too: an ink pill on a
                  // near-ink hero is a smudge, so the active route becomes a
                  // cream pill with ink type, which is the same contrast the
                  // other way round.
                  color: active
                    ? (light ? "var(--c-ink)" : "var(--c-paper)")
                    : (light ? "var(--c-cream)" : "var(--c-ink)"),
                  background: active
                    ? (light ? "var(--c-cream)" : "var(--c-ink)")
                    : "transparent",
                  transition: "background 120ms, color 200ms",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = light
                    ? "rgba(251,243,226,0.16)"
                    : "var(--c-cream-deep)";
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = "transparent";
                }}
              >
                {item.icon === "mic" && <MicIcon />}
                {item.icon === "star" && <StarIcon />}
                {item.label}
              </a>
            );
          })}
        </nav>

        <button
          className="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          aria-label="Open menu"
          style={{
            display: "none",
            // The menu button keeps its own solid surface in both states. A
            // ghost button over a photographic hero is the control people
            // cannot find, and this is the only way to the nav on a phone.
            background: "var(--c-paper)",
            border: "2px solid var(--c-ink)",
            borderRadius: 12,
            padding: "8px 14px",
            fontFamily: "var(--f-display)",
            fontWeight: 700,
          }}
        >
          {open ? "Close" : "Menu"}
        </button>
      </div>

      {open && (
        <div
          className="nav-mobile"
          style={{
            borderTop: "2px solid var(--c-ink)",
            background: "var(--c-paper)",
            padding: "14px 24px 22px",
          }}
        >
          {liveNavItems().map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={handleNav(item.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "12px 8px",
                borderBottom: "1px solid var(--c-line)",
                textDecoration: "none",
                color: "var(--c-ink)",
                fontFamily: "var(--f-display)",
                fontWeight: 600,
                fontSize: 18,
              }}
            >
              {item.icon === "mic" && <MicIcon />}
              {item.icon === "star" && <StarIcon />}
              {item.label}
            </a>
          ))}
        </div>
      )}

      {/* THE BREAKPOINT MOVES WITH THE EIGHTH ITEM, and that is the price of
          it. The seven-item nav is 862px wide and the row holds down to 981px,
          the narrowest width the desktop nav is ever shown at. Open Mic adds
          ~105px, and measured across 981-1280px that buys two regressions:
          sideways page scroll from 981-1010px, and the logo wrapping to a
          second line from 1024-1180px, which takes the header from 78px tall
          to 102px.

          Neither is acceptable and neither can be fixed by shaving the row --
          tightening the pill padding or the 4px gap would change how the whole
          header reads, on every page, to buy room for one link that is gone in
          a month. So the hamburger takes over below 1181px instead, for as long
          as the item is live. Above that the header is pixel-identical to the
          seven-item one; below it, nothing is cramped, the sheet already lists
          Open Mic, and the popup and footer link still reach the form.

          It reverts ITSELF. Same closesAt as everything else, so on October 27
          the item goes and the breakpoint goes back to 980 in the same render.
          Re-measure if a ninth permanent item is ever added. */}
      <style>{`
        @media (max-width: ${navBreakpoint()}px) {
          .nav-desktop { display: none !important; }
          .nav-mobile-toggle { display: inline-flex !important; }
        }
      `}</style>
    </header>
  );
}

function Marquee({ items }) {
  return (
    <div className="marquee" role="presentation">
      <div className="marquee-track">
        {Array.from({ length: 3 }).map((_, i) => (
          <React.Fragment key={i}>
            {items.map((t, j) => (
              <React.Fragment key={`${i}-${j}`}>
                <span>{t}</span>
                <span className="star">✦</span>
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function Footer({ setRoute }) {
  const handleNav = (id) => (e) => {
    e.preventDefault();
    setRoute(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  return (
    <footer
      style={{
        background: "var(--c-ink)",
        color: "var(--c-cream)",
        padding: "72px 0 40px",
      }}
    >
      <div className="container">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.4fr 1fr 1fr 1fr",
            gap: 40,
            marginBottom: 56,
          }}
          className="footer-grid"
        >
          <div>
            <img
              src="/img/ppr-logo.png"
              alt="Philadelphia Parks & Recreation Performing Arts logo"
              style={{
                width: 110,
                height: "auto",
                display: "block",
                marginBottom: 18,
              }}
            />
            <div
              className="display"
              style={{
                fontSize: 36,
                color: "var(--c-cream)",
                marginBottom: 16,
                lineHeight: 1,
              }}
            >
              Make some<br />noise.
            </div>
            <p style={{ color: "rgba(251,243,226,0.7)", fontSize: 14, maxWidth: 320 }}>
              The Performing Arts Office of Philadelphia Parks &amp; Recreation runs
              camps, programs, and showcases in neighborhoods across the city.
            </p>
            <SocialRow />
          </div>
          <FooterCol title="Programs">
            <FooterLink onClick={handleNav("camps")}>Summer Camps</FooterLink>
            <FooterLink onClick={handleNav("programs")}>Dance Programs</FooterLink>
            <FooterLink onClick={handleNav("programs")}>Drama Programs</FooterLink>
            <FooterLink onClick={handleNav("programs")}>Vocal Lessons</FooterLink>
            <FooterLink onClick={handleNav("mece-choir")}>MECE Choir</FooterLink>
            <FooterLink onClick={handleNav("workshop")}>Workshop Development</FooterLink>
          </FooterCol>
          <FooterCol title="Get Involved">
            {/* The only footer link on this site that comes and goes. The open
                mic is one night, and a permanent "Sign Up to Perform" pointing
                at a form for an evening that already happened is exactly the
                stale link every popup here retires itself to avoid -- the
                footer is on every page, so it would outlast all of them.
                Reads the same window.PPR_OPEN_MIC.closesAt as the popup and
                the page, through the same fail-closed guard: an unparseable
                date hides the link rather than pinning it up forever. */}
            {window.pprNoticeIsLive
              && window.PPR_OPEN_MIC
              && !window.PPR_OPEN_MIC.signupsClosed
              && window.pprNoticeIsLive(window.PPR_OPEN_MIC.closesAt, Date.now()) && (
              <FooterLink onClick={handleNav(window.PPR_OPEN_MIC.route || "openmic")}>
                {window.PPR_OPEN_MIC.name || "Open Mic Night"}
              </FooterLink>
            )}
            {window.pprNoticeIsLive
              && window.PPR_HOLIDAY_SHOW
              && !window.PPR_HOLIDAY_SHOW.signupsClosed
              && window.pprNoticeIsLive(window.PPR_HOLIDAY_SHOW.closesAt, Date.now()) && (
              <FooterLink onClick={handleNav(window.PPR_HOLIDAY_SHOW.route || "holidayshow")}>
                {window.PPR_HOLIDAY_SHOW.name || "Holiday Show"}
              </FooterLink>
            )}
            <FooterLink onClick={handleNav("about")}>About Us</FooterLink>
            <FooterLink onClick={handleNav("collaboration")}>Partners</FooterLink>
            <FooterLink onClick={handleNav("volunteer")}>Volunteer Opportunities</FooterLink>
            <FooterLink onClick={handleNav("gallery")}>Photo Gallery</FooterLink>
            <FooterLink onClick={handleNav("venice")}>Venice Island PAC</FooterLink>
          </FooterCol>
          <FooterCol title="Forms">
            <FooterLink onClick={handleNav("employment")}>Employment Application</FooterLink>
            <FooterLink onClick={handleNav("promote")}>Program Submission</FooterLink>
            <FooterLink onClick={handleNav("register")}>Registration Form</FooterLink>
            <FooterLink onClick={handleNav("testimonials")}>Testimonials</FooterLink>
          </FooterCol>
          <FooterCol title="Contact">
            <div style={{ color: "rgba(251,243,226,0.7)", fontSize: 14, lineHeight: 1.7 }}>
              1515 Arch Street<br />
              10th Floor<br />
              Philadelphia, PA 19102<br /><br />
              <a href="tel:+12156853585" style={{ color: "var(--c-accent)" }}>
                (215) 685-3585
              </a><br />
              <a href="mailto:Joseph.J.Pospiech@phila.gov" style={{ color: "var(--c-accent)" }}>
                Joseph.J.Pospiech@phila.gov
              </a>
            </div>
          </FooterCol>
        </div>

        <div
          style={{
            paddingTop: 28,
            borderTop: "1px solid rgba(251,243,226,0.2)",
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
            fontFamily: "var(--f-mono)",
            fontSize: 11,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "rgba(251,243,226,0.55)",
          }}
        >
          <div>© 2026 Philadelphia Department of Parks &amp; Recreation</div>
          <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
            <a href="/privacy" style={{ color: "inherit", textDecoration: "none" }}>Privacy</a>
            <a href="/terms" style={{ color: "inherit", textDecoration: "none" }}>Terms</a>
            <a href="https://fishtown.dev" target="_blank" rel="noopener" style={{ color: "inherit", textDecoration: "none", opacity: 0.8 }}>
              Made by Fishtown Development
            </a>
          </div>
        </div>
      </div>

      <style>{`
        .footer-icon {
          width: 36px; height: 36px; border-radius: 999px;
          background: var(--c-cream); color: var(--c-ink);
          display: inline-flex; align-items: center; justify-content: center;
          text-decoration: none; font-weight: 800; font-family: var(--f-display);
          border: 2px solid var(--c-cream);
          transition: transform 120ms;
        }
        .footer-icon:hover { transform: rotate(-6deg); background: var(--c-accent); }
        @media (max-width: 880px) {
          .footer-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 520px) {
          .footer-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </footer>
  );
}

function FooterCol({ title, children }) {
  return (
    <div>
      <div
        style={{
          fontFamily: "var(--f-mono)",
          fontSize: 11,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: "var(--c-accent)",
          marginBottom: 14,
        }}
      >
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
    </div>
  );
}

function FooterLink({ children, onClick, href }) {
  return (
    <a
      href={href || "#"}
      onClick={onClick}
      style={{
        color: "var(--c-cream)",
        textDecoration: "none",
        fontSize: 14.5,
        fontFamily: "var(--f-body)",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.color = "var(--c-accent)")}
      onMouseLeave={(e) => (e.currentTarget.style.color = "var(--c-cream)")}
    >
      {children}
    </a>
  );
}

function SocialRow() {
  const fb = useCMS("social.facebook", "");
  const ig = useCMS("social.instagram", "");
  const yt = useCMS("social.youtube", "");
  const items = [
    { url: fb, label: "Facebook", glyph: "f" },
    { url: ig, label: "Instagram", glyph: "◎" },
    { url: yt, label: "YouTube", glyph: "▶" },
  ].filter(i => i.url);
  if (items.length === 0) return null;
  return (
    <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
      {items.map(i => (
        <a key={i.label} href={i.url} className="footer-icon" aria-label={i.label} target="_blank" rel="noopener">{i.glyph}</a>
      ))}
    </div>
  );
}

Object.assign(window, { Header, Footer, Marquee, Logo, SocialRow });
